import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import clsx from 'clsx';
import { Container } from '@/components/Container';
import { AppLink } from '@/components/AppLink';
import { PORTAL_URL } from '@/config';
import { MOBILE_NAV, NAV } from '@/data/site';
import { logo } from '@/data/images';

export function Header() {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const toggle = useRef<HTMLButtonElement>(null);

  // The menu closes whenever the page changes.
  useEffect(() => setOpen(false), [pathname]);

  // Esc closes the menu and hands focus back to the button that opened it.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        toggle.current?.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  return (
    <header className="sticky top-0 z-20 border-b border-rule bg-white">
      <Container className="flex min-h-[72px] items-center justify-between gap-6">
        <Link to="/" className="flex min-h-11 min-w-0 flex-[0_1_auto] items-center">
          <img
            src={logo.src}
            alt={logo.alt}
            width={logo.width}
            height={logo.height}
            className="block h-[clamp(20px,2.6vw,30px)] w-auto max-w-full"
          />
        </Link>

        {/* Desktop: from 1180px up. Below that the Menu button takes over. */}
        <nav aria-label="Main" className="hidden items-center gap-[22px] text-[1rem] font-medium nav:flex">
          {NAV.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              className={({ isActive }) =>
                clsx(
                  'border-b-[3px] pt-6 pb-[21px] no-underline hover:text-teal-700',
                  isActive ? 'border-teal-700 text-teal-700' : 'border-transparent text-ink',
                )
              }
            >
              {l.label}
            </NavLink>
          ))}
          <AppLink
            href={PORTAL_URL}
            className="rounded-control border-[1.5px] border-navy-800 px-4 py-[9px] font-semibold text-navy-800 no-underline hover:bg-navy-800 hover:text-white"
          >
            Portal login
          </AppLink>
        </nav>

        <button
          ref={toggle}
          type="button"
          aria-expanded={open}
          aria-controls="mobile-nav"
          onClick={() => setOpen((o) => !o)}
          className="min-h-11 flex-none cursor-pointer rounded-control border-[1.5px] border-navy-800 bg-white px-3.5 text-[1rem] font-semibold text-navy-800 nav:hidden"
        >
          {open ? 'Close' : 'Menu'}
        </button>
      </Container>

      {open && (
        <nav
          id="mobile-nav"
          aria-label="Main"
          className="flex flex-col border-t border-rule bg-white px-gutter pt-2 pb-5 nav:hidden"
        >
          {MOBILE_NAV.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === '/'}
              className={({ isActive }) =>
                clsx(
                  'block border-b border-rule-subtle py-[13px] text-[1.125rem] font-medium no-underline',
                  isActive ? 'text-teal-700' : 'text-ink',
                )
              }
            >
              {l.label}
            </NavLink>
          ))}
          <AppLink
            href={PORTAL_URL}
            className="mt-4 block rounded-control bg-navy-800 p-[13px] text-center font-semibold text-white no-underline"
          >
            Portal login
          </AppLink>
        </nav>
      )}
    </header>
  );
}
